/**
 * Sentry initialization for the web frontend.
 *
 * Staging and production builds report to the SAME Sentry project and are
 * separated by the `environment` tag (set via VITE_ENVIRONMENT), so you can
 * filter events by env in the Sentry dashboard.
 *
 * Expected env vars:
 *   - VITE_SENTRY_DSN   — Sentry DSN (same as backend + mobile)
 *   - VITE_ENVIRONMENT  — "staging" or "production"
 *
 * When VITE_SENTRY_DSN is unset, Sentry is effectively disabled — useful for
 * local dev so we don't pollute the dashboard with developer errors.
 */

import * as Sentry from '@sentry/vue'
import type { App } from 'vue'
import type router from '@/router'

type Router = typeof router

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined
const ENVIRONMENT =
  (import.meta.env.VITE_ENVIRONMENT as string | undefined) || 'development'

let initialized = false

export function initSentry(app: App, router: Router): void {
  if (initialized) return

  if (!DSN) {
    // eslint-disable-next-line no-console
    console.log('[Sentry] DSN not configured — Sentry disabled on this build')
    return
  }

  Sentry.init({
    app,
    dsn: DSN,
    environment: ENVIRONMENT,
    // Never auto-send PII — user context is set explicitly on login.
    sendDefaultPii: false,
    // Performance sampling: low in production to keep quota sane; full in staging
    tracesSampleRate: ENVIRONMENT === 'production' ? 0.1 : 1.0,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
    ],
    // Don't ship dev errors
    enabled: ENVIRONMENT !== 'development',
  })

  initialized = true
  // eslint-disable-next-line no-console
  console.log(`[Sentry] Initialized for environment=${ENVIRONMENT}`)
}

/**
 * Associate the signed-in user with subsequent events.
 */
export function setSentryUser(user: {
  id: string
  email?: string
  roles?: string[]
}): void {
  if (!initialized) return
  Sentry.setUser({
    id: user.id,
    email: user.email,
    // Roles go into user.data (Sentry shows this under "User" in event details)
    ...(user.roles ? { roles: user.roles.join(',') } : {}),
  })
}

export function clearSentryUser(): void {
  if (!initialized) return
  Sentry.setUser(null)
}

/**
 * Add a breadcrumb entry to the Sentry event timeline.
 */
export function addSentryBreadcrumb(
  message: string,
  category: string = 'app',
  data?: Record<string, unknown>
): void {
  if (!initialized) return
  Sentry.addBreadcrumb({
    message,
    category,
    level: 'info',
    data,
    timestamp: Date.now() / 1000,
  })
}

/**
 * Capture a handled error with optional tags + extra context.
 */
export function captureHandledError(
  error: unknown,
  context?: { tags?: Record<string, string>; extra?: Record<string, unknown> }
): void {
  if (!initialized) return
  Sentry.captureException(error, (scope) => {
    if (context?.tags) {
      Object.entries(context.tags).forEach(([k, v]) => scope.setTag(k, v))
    }
    if (context?.extra) {
      Object.entries(context.extra).forEach(([k, v]) => scope.setExtra(k, v))
    }
    return scope
  })
}

export { Sentry }
